import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle, ArrowRight, Phone, Globe, Shield, Zap, DollarSign, Bot, Users, Layers, Target, Gauge } from 'lucide-react';
import SiteFooter from './SiteFooter';
import SEOHead from './SEOHead';

const FEATURES = [
  { name: 'Call Tracking (CallRail)', us: true, them: false, category: 'tracking' },
  { name: 'Dynamic Number Insertion (DPNI)', us: true, them: false, category: 'tracking' },
  { name: 'Call Recording & Attribution', us: true, them: false, category: 'tracking' },
  { name: 'Citation Builder / Tracker', us: true, them: false, category: 'citations' },
  { name: 'CRM & Pipeline Management', us: false, them: true, category: 'crm' },
  { name: 'Funnel Builder', us: false, them: true, category: 'crm' },
  { name: 'Course / Membership Builder', us: false, them: true, category: 'crm' },
  { name: 'White-Label Client Dashboard', us: true, them: true, category: 'agency' },
  { name: 'White-Label Mobile App', us: false, them: true, category: 'agency' },
  { name: 'Client Impersonation (Admin View)', us: true, them: false, category: 'agency' },
  { name: 'Subscription Dunning System', us: true, them: false, category: 'billing' },
  { name: 'Failed Payment Auto-Recovery', us: true, them: false, category: 'billing' },
  { name: '3,750+ Programmatic SEO Pages', us: true, them: false, category: 'seo' },
  { name: 'AI-Powered Blog (Daily Auto-Posts)', us: true, them: false, category: 'content' },
  { name: 'Schema Stacking (7 Types)', us: true, them: false, category: 'seo' },
  { name: 'IndexNow + Google Indexing API', us: true, them: false, category: 'seo' },
  { name: 'Review Monitoring & Generation', us: true, them: true, category: 'reputation' },
  { name: 'SMS Campaigns ($0.049/msg via Telnyx)', us: true, them: true, category: 'marketing' },
  { name: 'Email Marketing', us: true, them: true, category: 'marketing' },
  { name: 'Appointment Booking', us: false, them: true, category: 'crm' },
  { name: 'Stripe/PayPal Billing', us: true, them: true, category: 'billing' },
  { name: 'SaaS Mode (Resell as Your Own)', us: false, them: true, category: 'agency' },
  { name: 'AI Conversational Bot', us: false, them: true, category: 'ai' },
  { name: 'Real-Time Lead Attribution', us: true, them: false, category: 'tracking' },
];

const PRICING = [
  { label: 'GHL Starter', price: '$97/mo', features: '3 sub-accounts, basic CRM, no API' },
  { label: 'GHL Unlimited', price: '$297/mo', features: 'Unlimited accounts, API, white-label desktop' },
  { label: 'GHL SaaS Pro', price: '$497/mo', features: 'SaaS mode, mobile app, AI bot, priority support' },
];

const COLORS = { primary: '#002FA7', dark: '#0a0f1e', accent: '#FFCC00' };

export default function ComparisonGoHighLevel() {
  const { t } = useTranslation('compare');
  const usWins = FEATURES.filter(f => f.us && !f.them).length;
  const theyWin = FEATURES.filter(f => !f.us && f.them).length;
  const both = FEATURES.filter(f => f.us && f.them).length;

  return (
    <>
      <SEOHead
        title="LocalBranding Pro vs GoHighLevel — The Honest Comparison 2026"
        description="Compare LocalBranding Pro and GoHighLevel (GHL) side-by-side. GHL is a powerful CRM but lacks call tracking, citation building, and programmatic SEO. See which fits your agency."
        canonical="https://localbrandingpro.com/compare/gohighlevel"
        keywords="LocalBranding Pro vs GoHighLevel, GoHighLevel alternative, GHL alternative, local SEO vs CRM, agency marketing platform"
      />
      <div style={{ fontFamily: "'Outfit', 'IBM Plex Sans', sans-serif" }}>
        <section className="relative overflow-hidden" style={{ background: COLORS.dark }}>
          <div className="absolute inset-0 opacity-10" style={{ background: `radial-gradient(circle at 60% 40%, #7c3aed, transparent 50%)` }} />
          <div className="max-w-6xl mx-auto px-6 py-20 relative z-10 text-center">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] mb-4 px-4 py-1.5 rounded-full" style={{ background: `${COLORS.accent}20`, color: COLORS.accent }}>
              CRM vs Local Marketing 2026
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-testid="comparison-title">
              LocalBranding Pro
              <span className="block text-2xl sm:text-3xl font-normal text-slate-400 mt-2">{t('gohighlevel.titleSub')}</span>
            </h1>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-8">
              GoHighLevel is a powerful CRM for funnels and automation. But for local marketing agencies that need call tracking, citations, and SEO — it's missing critical pieces.
            </p>
            <div className="flex flex-wrap justify-center gap-6 mb-8">
              <div className="text-center px-6 py-3 rounded-xl bg-white/5 border border-white/10">
                <p className="text-3xl font-bold" style={{ color: COLORS.accent }}>{usWins}</p>
                <p className="text-xs text-slate-400 mt-1">Features Only We Have</p>
              </div>
              <div className="text-center px-6 py-3 rounded-xl bg-white/5 border border-white/10">
                <p className="text-3xl font-bold text-white">{both}</p>
                <p className="text-xs text-slate-400 mt-1">Shared Features</p>
              </div>
              <div className="text-center px-6 py-3 rounded-xl bg-white/5 border border-white/10">
                <p className="text-3xl font-bold text-slate-500">{theyWin}</p>
                <p className="text-xs text-slate-400 mt-1">GoHighLevel Only</p>
              </div>
            </div>
            <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 rounded-lg text-white font-bold text-lg transition-all hover:scale-105" style={{ background: COLORS.primary }} data-testid="cta-hero">
              Try LocalBranding Pro Free <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </section>

        <section className="py-16 bg-white" data-testid="comparison-table-section">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-2" style={{ color: COLORS.dark }}>Side-by-side feature comparison</h2>
            <p className="text-center text-slate-500 text-sm mb-10">Different strengths for different needs — here's the full breakdown</p>
            <div className="rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <table className="w-full text-sm" data-testid="comparison-table">
                <thead><tr className="bg-slate-50">
                  <th className="text-left px-6 py-4 font-semibold text-slate-700">Feature</th>
                  <th className="text-center px-6 py-4 font-bold" style={{ color: COLORS.primary }}>LocalBranding Pro</th>
                  <th className="text-center px-6 py-4 font-semibold text-slate-500">GoHighLevel</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {FEATURES.map((f, i) => (
                    <tr key={i} className={`${f.us && !f.them ? 'bg-blue-50/30' : ''} hover:bg-slate-50`}>
                      <td className="px-6 py-3.5 text-slate-700">{f.name}</td>
                      <td className="px-6 py-3.5 text-center">{f.us ? <CheckCircle2 className="h-5 w-5 inline text-emerald-500" /> : <XCircle className="h-5 w-5 inline text-slate-300" />}</td>
                      <td className="px-6 py-3.5 text-center">{f.them ? <CheckCircle2 className="h-5 w-5 inline text-emerald-500" /> : <XCircle className="h-5 w-5 inline text-slate-300" />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-8 p-6 rounded-xl bg-amber-50 border border-amber-200">
              <h3 className="font-bold text-amber-900 mb-2">The honest take</h3>
              <p className="text-amber-800 text-sm leading-relaxed">GoHighLevel is excellent if you need a CRM with funnels, appointment booking, and automation. But if your agency focuses on <strong>local SEO, call tracking, and citation management</strong>, GHL doesn't cover those. Many agencies use both — GHL for the CRM, and LocalBranding Pro for the local marketing stack.</p>
            </div>
          </div>
        </section>

        <section className="py-16" style={{ background: '#f8fafc' }}>
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10" style={{ color: COLORS.dark }}>Where LocalBranding Pro wins</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Phone, title: 'Call Tracking Built-In', desc: 'GHL has basic call features, but no CallRail-level attribution. We show exactly which page, keyword, or ad drove each call.' },
                { icon: Globe, title: 'Citation Management', desc: 'GHL has zero citation building or tracking. We integrate BrightLocal\'s Citation Tracker directly into your dashboard.' },
                { icon: Bot, title: 'AI Visibility Scanning', desc: 'See how your business appears in AI search results. GHL\'s AI is for chatbots — ours tracks your AI discoverability.' },
                { icon: Target, title: '3,750 SEO Pages', desc: 'Programmatic city + industry landing pages with schema stacking. GHL builds funnels, not SEO pages.' },
                { icon: Shield, title: 'Subscription Dunning', desc: 'GHL SaaS mode handles billing but not failed payment recovery. Our 4-stage dunning system protects your revenue.' },
                { icon: DollarSign, title: 'Lower SMS Costs', desc: 'We use Telnyx at $0.049/msg. GHL charges usage-based SMS on top of your subscription with less transparency.' },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-xl p-6 border border-slate-200 hover:shadow-md transition-shadow">
                  <item.icon className="h-8 w-8 mb-4" style={{ color: COLORS.primary }} />
                  <h3 className="font-bold text-lg mb-2" style={{ color: COLORS.dark }}>{item.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10" style={{ color: COLORS.dark }}>{t('gohighlevel.pricingTitle')}</h2>
            <div className="grid md:grid-cols-3 gap-6 mb-10">
              {PRICING.map((p, i) => (
                <div key={i} className="rounded-xl border border-slate-200 p-6 text-center">
                  <p className="text-sm font-semibold text-slate-500 mb-2">{p.label}</p>
                  <p className="text-3xl font-bold mb-1" style={{ color: COLORS.dark }}>{p.price}</p>
                  <p className="text-xs text-slate-400">{p.features}</p>
                  <p className="text-xs text-red-400 mt-2">+ usage-based SMS/call fees</p>
                </div>
              ))}
            </div>
            <div className="rounded-xl p-8 text-center" style={{ background: COLORS.primary }}>
              <p className="text-white/80 text-sm mb-2">LocalBranding Pro</p>
              <p className="text-4xl font-bold text-white mb-2">Local marketing + call tracking. One price.</p>
              <p className="text-white/60 text-sm mb-6">Use alongside GHL or replace it entirely — your call</p>
              <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-3 rounded-lg font-bold transition-all hover:scale-105" style={{ background: COLORS.accent, color: COLORS.dark }} data-testid="cta-pricing">
                Get Started Free <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16" style={{ background: COLORS.dark }}>
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">The best agencies use specialized tools</h2>
            <p className="text-slate-400 mb-8">GHL for your CRM. LocalBranding Pro for your local marketing. Or consolidate everything into one platform.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="px-8 py-4 rounded-lg font-bold text-lg transition-all hover:scale-105" style={{ background: COLORS.accent, color: COLORS.dark }}>Start Free Trial</Link>
              <Link to="/compare/vendasta" className="px-8 py-4 rounded-lg font-bold text-lg border border-white/20 text-white hover:bg-white/10">vs Vendasta</Link>
            </div>
          </div>
        </section>
        <SiteFooter />
      </div>
    </>
  );
}
